import React, { Component } from 'react';
import { gql, graphql, compose } from 'react-apollo';
import { connect } from 'react-redux';
import { shape, bool, func, arrayOf, string } from 'prop-types';
import { operations as segmentCreationOperations } from 'state/ducks/segmentCreation';
import { operations as mapOperations } from 'state/ducks/map';
import { first, last } from 'lodash';
import coord from 'propTypes/coord';

import filterAvailableLocations from 'helpers/filterAvailableLocations';

import { LiveMapQuery } from 'components/Map/Live';
import LoadingIndicator from 'components/LoadingIndicator';
import LocationStatusIcon from 'components/LocationStatusIcon';
import LocationsHeader from './LocationsHeader';
import List from '../List';
import ListItem from '../ListItem';
import Footer from '../Footer';
import Title from '../Title';

import styles from './CreateSegment.scss';

const API_ERROR = '__error__';

const CreateSegmentQuery = gql`
  query Locations {
    locations {
      id
      name
      trackingDevice {
        id
        status
      }
    }
    segments {
      id
      from {
        id
        name
      }
      to {
        id
        name
      }
    }
  }
`;

const CreatePathSuggestionsMutation = gql`
  mutation createPathSuggestions($fromId: ID!, $toId: ID!) {
    createPathSuggestions(fromId: $fromId, toId: $toId) {
      id
      path
      distance
      expectedTravelTime
    }
  }
`;

const CreateSegmentMutation = gql`
  mutation createSegment($fromId: ID!, $toId: ID!, $pathSuggestionId: ID!) {
    createSegment(fromId: $fromId, toId: $toId, pathSuggestionId: $pathSuggestionId) {
      id
    }
  }
`;

class CreateSegment extends Component {
  state = {
    waitForResponse: false,
  };
  componentWillReceiveProps(nextProps) {
    const {
      selectedLocations,
      pathSuggestions,
      createPathSuggestions,
      setPathSuggestions,
    } = nextProps;

    const { waitForResponse } = this.state;
    const hasTwoLocations = selectedLocations && selectedLocations.length === 2;

    if (!waitForResponse && hasTwoLocations && !pathSuggestions) {
      createPathSuggestions()
        .then(result => {
          setPathSuggestions(result.data.createPathSuggestions);
          this.setState({ waitForResponse: false });
        })
        .catch(err => {
          this.setState({ waitForResponse: false });
          // eslint-disable-next-line no-console
          console.error(`Failed to create path suggestions: ${err.message}`);
        });

      this.setState({ waitForResponse: true });
    }
  }

  handleDone = () => {
    const { createSegment, setCreatedSegmentId } = this.props;
    createSegment()
      .then(result => {
        setCreatedSegmentId(result.data.createSegment.id);
        this.setState({ waitForResponse: false });
      })
      .catch(err => {
        this.setState({ waitForResponse: false });
        // eslint-disable-next-line no-console
        console.error(`Failed to create new segment: ${err.message}`);
      });

    this.setState({ waitForResponse: true });
  };

  handleClose = () => {
    const { finishCreateSegment } = this.props;
    finishCreateSegment();
  };

  handleCancel = () => {
    const { finishCreateSegment } = this.props;
    finishCreateSegment();
  };

  handleNew = () => {
    const { clearCreateSegment } = this.props;
    clearCreateSegment();
  };

  handleClick = locationId => {
    this.props.selectLocation(locationId);
  };

  render() {
    const {
      data: { loading, locations, segments },
      deselectLocation,
      selectedLocations,
      selectedPathSuggestion,
      createdSegmentId,
      currentHover,
      enterEntity,
      leaveEntity,
    } = this.props;

    if (loading) {
      return <LoadingIndicator />;
    }

    let step = null;
    const hasNoLocations = !selectedLocations || selectedLocations.length === 0;
    const hasTwoLocations = selectedLocations && selectedLocations.length === 2;

    if (hasNoLocations) {
      step = 1;
    } else if (selectedLocations && !hasTwoLocations) {
      step = 2;
    } else if (hasTwoLocations) {
      step = 3;

      if (selectedPathSuggestion) {
        step = 4;
      }

      if (createdSegmentId) {
        step = 5;
      }
    }

    // Do not show the to/from header if nothing was selected
    const showHeader = step > 1;

    // Show the list if to/from selection is not complete
    const showList = !hasTwoLocations;

    let fromLocation = null;
    let toLocation = null;
    const isFinalStep = step === 4;
    const isFinished = Boolean(createdSegmentId);
    const hasError = createdSegmentId === API_ERROR;

    if (selectedLocations) {
      fromLocation = locations.find(({ id }) => id === first(selectedLocations, 'id'));
    }

    // If have selected one location, only show possible next steps
    const filteredLocations = filterAvailableLocations(locations, segments, selectedLocations);

    if (hasTwoLocations) {
      toLocation = locations.find(({ id }) => id === last(selectedLocations, 'id'));
    }

    const { waitForResponse } = this.state;

    const createLocationListItem = location => {
      const isActive = currentHover && currentHover.id === location.id;
      const locationStatus = location.trackingDevice.status;
      return (
        <ListItem
          key={location.id}
          id={location.id}
          isActive={isActive}
          status={locationStatus}
          onMouseOver={() => enterEntity({ id: location.id, type: 'location' })}
          onMouseOut={() => leaveEntity({ id: location.id, type: 'location' })}
          onClick={() => this.handleClick(location.id, locations)}
        >
          <LocationStatusIcon status={locationStatus} className={styles.locationIcon} />
          {location.name}
        </ListItem>
      ); // Don't show locations that are found in existing segments
    };

    // We have already filtered out already connecting segments for the panel
    // On the map we want to show the current selected marker, in the panel we do not.
    // That's why we do an extra filter in the panel.
    const availableLocations =
      selectedLocations.length > 0
        ? filteredLocations.filter(location => {
            // Don't show a location we have already selected
            if (selectedLocations.includes(location.id)) {
              return false;
            }
            // Otherwise just show the location in the list
            return true;
          })
        : filteredLocations;

    return (
      <div className={styles.main}>
        <div className={styles.top}>
          <Title>Create new segment</Title>
          {showHeader &&
            <LocationsHeader
              fromLocation={fromLocation}
              toLocation={toLocation}
              removeLocation={deselectLocation}
              isFinished={isFinished}
            />}
          {hasNoLocations
            ? <h3 className={styles.subtitle}>Select origin</h3>
            : !hasTwoLocations ? <h3 className={styles.subtitle}>Select destination</h3> : null}
        </div>
        <div className={styles.content}>
          {showList &&
            <List className={styles.list}>
              {availableLocations.map(createLocationListItem)}
            </List>}

          {isFinished
            ? hasError
              ? <div className={styles.text}>An error occurred. This has been reported.</div>
              : <div className={styles.text}>You have successfully created a new segment. </div>
            : null}
        </div>

        <Footer
          className={styles.footer}
          onCancel={this.handleCancel}
          onDone={this.handleDone}
          onNew={this.handleNew}
          onClose={this.handleClose}
          isFinalStep={isFinalStep}
          isFinished={isFinished}
          waitForResponse={waitForResponse}
        />
      </div>
    );
  }
}

CreateSegment.propTypes = {
  data: shape({
    loading: bool.isRequired,
    location: shape({}),
    segments: arrayOf(
      shape({
        id: string,
        name: string,
      })
    ),
  }),
  selectedLocations: arrayOf(string),
  pathSuggestions: arrayOf(
    shape({
      id: string.isRequired,
      path: arrayOf(coord).isRequired,
    })
  ),
  selectedPathSuggestion: shape({
    id: string.isRequired,
  }),
  selectLocation: func.isRequired,
  deselectLocation: func.isRequired,
  createdSegmentId: string,
  setPathSuggestions: func.isRequired,
  setCreatedSegmentId: func.isRequired,

  createSegment: func.isRequired,
  finishCreateSegment: func.isRequired,
  clearCreateSegment: func.isRequired,
  createPathSuggestions: func.isRequired,
  currentHover: shape({ id: string }),
  enterEntity: func.isRequired,
  leaveEntity: func.isRequired,
};

const mapStateToProps = ({ map, segmentCreation }) => ({
  currentHover: map.currentHover,
  selectedLocations: segmentCreation.selectedLocations,
  pathSuggestions: segmentCreation.pathSuggestions,
  selectedPathSuggestion: segmentCreation.selectedPathSuggestion,
  createdSegmentId: segmentCreation.createdSegmentId,
});

const ConnectedCreateSegmentWithData = compose(
  connect(mapStateToProps, { ...mapOperations, ...segmentCreationOperations }),
  graphql(CreateSegmentQuery, {}),
  graphql(CreatePathSuggestionsMutation, {
    props: ({ mutate, ownProps: { selectedLocations } }) => ({
      createPathSuggestions: () =>
        mutate({
          variables: {
            fromId: first(selectedLocations),
            toId: last(selectedLocations),
          },
        }),
    }),
  }),
  graphql(CreateSegmentMutation, {
    props: ({ mutate, ownProps: { selectedLocations, selectedPathSuggestion } }) => ({
      createSegment: () =>
        mutate({
          variables: {
            fromId: first(selectedLocations),
            toId: last(selectedLocations),
            pathSuggestionId: selectedPathSuggestion.id,
          },
          refetchQueries: [
            {
              query: LiveMapQuery,
            },
          ],
        }),
    }),
  })
)(CreateSegment);

export default ConnectedCreateSegmentWithData;



// WEBPACK FOOTER //
// ./src/components/MapPanel/CreateSegment/index.js